import React, { useMemo, useState } from "react";

// Iconos reales de Instagram
const IconLike = () => (
  <svg aria-label="Me gusta" fill="none" height="24" width="24" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54l1.45 1.32 1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z"/>
  </svg>
);

const IconComment = () => (
  <svg aria-label="Comentar" fill="none" height="24" width="24" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 12c0 5.25 4.67 9.5 10.5 9.5 1.7 0 3.3-.35 4.7-.98L21 22l-1.15-3.44c1.12-1.66 1.65-3.6 1.65-5.56 0-5.25-4.67-9.5-10.5-9.5S3 6.75 3 12z"/>
  </svg>
);

const IconShare = () => (
  <svg aria-label="Compartir publicación" fill="none" height="24" width="24" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const IconSave = () => (
  <svg aria-label="Guardar" fill="none" height="24" width="24" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polygon points="19 21 12 16.5 5 21 5 3 19 3 19 21"/>
  </svg>
);

type Props = {
  imageUrl: string;
  caption: string;
  username?: string;
  avatarUrl?: string;
  postedAtLabel?: string;          // p.ej. "Ahora", "1 h", "Ayer"
  showActions?: boolean;           // corazon/coment/compartir/guardar (decorativo)
  maxCaptionChars?: number;        // límite antes de mostrar "[más]"
  fullWidth?: boolean;             // usar todo el ancho disponible
};

const fallbackAvatar =
  "https://ui-avatars.com/api/?name=IG&background=BBB&color=FFF&size=128";

// Límites de IG feed en ratio (ancho/alto)
const MIN_LANDSCAPE = 1.91; // 1.91:1 (más ancho permitido)
const MAX_PORTRAIT = 0.8;   // 4:5 (más alto permitido)

const clampIgRatio = (r: number) => {
  // Si es muy ancho, fuerzo a 1.91; si es muy alto, fuerzo a 0.8; si no, dejo el ratio real
  if (r > MIN_LANDSCAPE) return MIN_LANDSCAPE;
  if (r < MAX_PORTRAIT) return MAX_PORTRAIT;
  return r;
};

const InstagramPostPreview: React.FC<Props> = ({
  imageUrl,
  caption,
  username = "tu_cuenta",
  avatarUrl,
  postedAtLabel = "Ahora",
  showActions = true,
  maxCaptionChars = 140,
  fullWidth = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [containerRatio, setContainerRatio] = useState<number>(1); // por defecto cuadrado

  const handleImageError = () => {
    setImgOk(false);
  };

  const handleImageLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    const img = e.currentTarget;
    const naturalW = img.naturalWidth || 0;
    const naturalH = img.naturalHeight || 0;
    if (naturalW > 0 && naturalH > 0) {
      const realRatio = naturalW / naturalH; // ancho/alto de la imagen real
      setContainerRatio(clampIgRatio(realRatio));
    } else {
      setContainerRatio(1); // fallback
    }
    setImgOk(true);
  };

  // Convertir \n a saltos de línea reales y resaltar @menciones y #hashtags
  const parsedCaption = useMemo(() => {
    const text = (caption ?? "").replace(/\\n/g, '\n'); // Convertir \n a saltos reales
    const withLinks = text
      .replace(
        /(^|\s)(@[a-zA-Z0-9_\.]+)/g,
        '$1<span class="text-blue-600 hover:underline">$2</span>'
      )
      .replace(
        /(^|\s)(#[\w\d_]+)(?=\s|$)/g,
        '$1<span class="text-blue-600 hover:underline">$2</span>'
      );
    return withLinks;
  }, [caption]);

  const processedText = (caption ?? "").replace(/\\n/g, '\n');
  const needsClamp = processedText.length > maxCaptionChars;
  const shortCaption = useMemo(() => {
    if (!needsClamp) return parsedCaption;
    const sliced = processedText.slice(0, maxCaptionChars).trimEnd() + "…";
    return sliced
      .replace(
        /(^|\s)(@[a-zA-Z0-9_\.]+)/g,
        '$1<span class="text-blue-600 hover:underline">$2</span>'
      )
      .replace(
        /(^|\s)(#[\w\d_]+)(?=\s|$)/g,
        '$1<span class="text-blue-600 hover:underline">$2</span>'
      );
  }, [processedText, parsedCaption, maxCaptionChars, needsClamp]);

  return (
    <div className={`w-full ${fullWidth ? '' : 'max-w-md mx-auto'} bg-white rounded-2xl border border-gray-200`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <img
            src={avatarUrl || fallbackAvatar}
            onError={(e) => {
              (e.target as HTMLImageElement).src = fallbackAvatar;
            }}
            alt={`${username} avatar`}
            className="w-8 h-8 rounded-full object-cover"
          />
          <span className="text-sm font-semibold">Cuenta de {username}</span>
        </div>
        <button
          type="button"
          aria-label="Opciones"
          className="text-gray-500"
        >
          •••
        </button>
      </div>

      {/* Media (se adapta al ratio como Instagram) */}
      <div className="bg-gray-100">
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: String(containerRatio), // ratio dinámico
          }}
        >
          {imgOk ? (
            <img
              src={imageUrl}
              alt="Post"
              className="absolute inset-0 w-full h-full object-cover"
              onError={handleImageError}
              onLoad={handleImageLoad}
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <span className="text-sm">No se pudo cargar la imagen</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions (decorativas) */}
      {showActions && (
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="flex items-center gap-4">
            <button className="text-gray-900" aria-label="Me gusta">
              <IconLike />
            </button>
            <button className="text-gray-900" aria-label="Comentar">
              <IconComment />
            </button>
            <button className="text-gray-900" aria-label="Compartir">
              <IconShare />
            </button>
          </div>
          <button className="text-gray-900" aria-label="Guardar">
            <IconSave />
          </button>
        </div>
      )}

      {/* Caption */}
      <div className="px-4 py-3">
        <p className="text-sm">
          <span className="font-semibold mr-1">Tu cuenta de Instagram</span>
          <span
            className="whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{
              __html: expanded || !needsClamp ? parsedCaption : shortCaption,
            }}
          />
          {needsClamp && !expanded && (
            <>
              {" "}
              <button
                type="button"
                className="text-gray-500"
                onClick={() => setExpanded(true)}
              >
                [más]
              </button>
            </>
          )}
        </p>
        <p className="text-[11px] text-gray-400 uppercase mt-2 tracking-wide">
          {postedAtLabel}
        </p>
      </div>
    </div>
  );
};

export default InstagramPostPreview;
